import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addMinutes, getDateFromOffset } from '../../../helpers/dates';
import { useHistory } from 'react-router-dom';
import { convertTime12to24 } from '../../../helpers/dates';
import { useTranslation } from 'react-i18next';
import { closestIndexTo, format } from 'date-fns';
import Header from '../../../components/Common/Header/Header';
import ContentApp from '../../../components/ContentApp';
import Button from '../../../components/Common/Button/Button';
import styles from '../reservation.module.css';
import SuccessScreen from '../../Common/SuccessScreen';
import { WelletContext } from '../../../context/WelletContext';
import { Spinner } from '../../../components/Common/Spinner/Spinner';
import EventHeader from '../../../components/Common/EventHeader/EventHeader';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import CustomerSpecialRequest from '../../../components/ReservationCheck/CustomerSpecialRequest/CustomerSpecialRequest';
import BookSelectorModify from '../../../components/BookSelector/BookSelectorModify';
import ErrorBox from '../../../components/Common/ErrorBox/ErrorBox';
import { lockSchedule } from '../../../services/reservations/reservationsService';
import { CircleSpinner } from 'react-spinners-kit';
import { setCanRefresh } from '../../../actions/appActions';

const ModifyReservation = () => {
  const { t } = useTranslation();
  const welletContext = useContext(WelletContext);
  const language = useSelector((state) => state.app.language);
  const history = useHistory();
  const reservationInfo = history.location.state;

  const [restaurantId] = useState(reservationInfo?.showId);
  const [loading, setLoading] = useState(true);
  const [today, setToday] = useState(null);
  const [compareToday, setCompareToday] = useState(null);
  const [selectedPax, setSelectedPax] = useState(reservationInfo?.paxs);
  const [selectedDate, setSelectedDate] = useState(
    format(new Date(reservationInfo?.reservationDate), 'yyyy-MM-dd'),
  );
  const [selectedPerformance, setSelectedPerformance] = useState(null);
  const [intervals, setIntervals] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [modifyingReservation, setModifyingReservation] = useState(false);
  const [response, setResponse] = useState(null);
  const [selectedDateBook, setSelectedDateBook] = useState(null);
  const [specialRequest, setSpecialRequest] = useState(reservationInfo?.comments || '');
  const [loadingIntervals, setLoadingIntervals] = useState(true);
  const [scheduleNotAvailable, setScheduleNotAvailable] = useState(false);
  const [platForm, setPlatForm] = useState(false);
  const [externalPlaftorm, setExternalPlaftorm] = useState(null);
  const [firstScheduleLoaded, setFirstScheduleLoaded] = useState(false);
  const [availableAttributes, setAvailableAttributes] = useState([]);
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const dispatch = useDispatch();

  const appCanRefresh = useSelector((state) => state.app.canRefresh);
  const swiperTimeRef = useRef(null);
  const swiperAttributesRef = useRef(null);
  useEffect(() => {
    if (appCanRefresh) {
      dispatch(setCanRefresh(false));
      return () => dispatch(setCanRefresh(true));
    }
  }, []);
  useEffect(() => {
    if (!reservationInfo && !modifyingReservation) {
      history.goBack();
    }
  }, [reservationInfo, modifyingReservation]);

  const getShow = async () => {
    try {
      const result = await welletContext.apis.tickets.get(`/shows/get/`, {
        params: { showId: restaurantId },
      });

      const todayDate = new Date(getDateFromOffset(result.data.utcOffset));
      const minutes = todayDate.getMinutes();
      todayDate.setMinutes(minutes === 0 ? minutes : minutes + (15 - (minutes % 15)), 0, 0);

      setToday(todayDate);
      setCompareToday(addMinutes(todayDate, 20));
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const getExternalPlatform = async () => {
    if (!platForm) return;
    try {
      const result = await welletContext.apis.tickets.get(
        `/shows/${restaurantId}/external-plaftorm/config?reservationType=RESTAURANT`,
      );
      setExternalPlaftorm(result.data);
    } catch (error) {
      console.error(error);
    }
  };

  const setClosedTime = (date, intervals) => {
    if (swiperTimeRef.current && swiperTimeRef.current.swiper) {
      const index = closestIndexTo(date, intervals);
      if (index !== null && index !== -1) {
        swiperTimeRef.current.swiper.slideTo(index);
      }
    }
  };

  const getSchedule = async (date, paxs = null, hour = null) => {
    setLoadingIntervals(true);
    try {
      let params = `showId=${restaurantId}&date=${date}&reservationType=RESTAURANT&details=true`;
      if (hour) params += `&hour=${hour}`;
      if (paxs) params += `&paxs=${paxs}`;

      const result = await welletContext.apis.tickets.get(`/shows/GetSchedule?${params}`);
      setPlatForm(result.data.scheduleFromExternalPlatform);

      const rawSchedules = result.data?.schedules || [];

      const formattedTimes = rawSchedules.map((time) => {
        const time24 = new Date(date + 'T' + convertTime12to24(time.timeStart));
        if (time.timeMinutesStart >= 0 && time.timeMinutesStart <= 360) {
          time24.setDate(time24.getDate() + 1);
        }
        return {
          time24,
          soldOut: time.isSoldOut,
          availabilityRefId: time.availabilityRefId,
          attributes: time.attributes || [],
        };
      });

      formattedTimes.sort((a, b) => a.time24 - b.time24);

      const allIntervals = formattedTimes.map(
        ({ time24, soldOut, availabilityRefId, attributes }) => ({
          time: time24,
          soldOut,
          availabilityRefId,
          attributes,
        }),
      );

      const filteredIntervals = allIntervals.filter((x) => !x.soldOut);

      setIntervals(filteredIntervals);
    } catch (error) {
      console.error(error);
      setIntervals([]);
    } finally {
      setLoadingIntervals(false);
      setFirstScheduleLoaded(true);
    }
  };

  useEffect(() => {
    const reservationDate = new Date(reservationInfo?.reservationDate);
    setSelectedDateBook(reservationDate);
    getShow();
  }, []);

  useEffect(() => {
    if (platForm) {
      getExternalPlatform();
    }
  }, [platForm]);

  useEffect(() => {
    if (selectedDate && selectedPax && today !== null) {
      getSchedule(selectedDate, selectedPax);
    }
  }, [selectedDate, selectedPax, today]);

  useEffect(() => {
    if (intervals.length && selectedDate) {
      const referenceDate = new Date(
        `${selectedDate}T${
          selectedPerformance || reservationInfo?.reservationDate?.split('T')[1]?.substring(0, 5)
        }`,
      );
      setClosedTime(
        referenceDate,
        intervals.map((item) => item.time),
      );
    }
  }, [intervals]);

  const onBookSelectorChange = (e) => {
    const newDate = e.date;
    const newPax = e.pax;
    setSelectedDate(newDate);
    setSelectedPax(newPax);
    setSelectedDateBook(new Date(newDate + 'T' + e.time));
    setSelectedPerformance(null);
    setSelectedTime(null);
    setSelectedAttribute(null);
    setAvailableAttributes([]);
    getSchedule(newDate, newPax, e.time);
  };

  const attributeTranslations = {
    default: t('translateAttribute.default'),
    counter: t('translateAttribute.counter'),
    highTop: t('translateAttribute.highTop'),
    outdoor: t('translateAttribute.outdoor'),
    bar: t('translateAttribute.bar'),
  };

  const onTimeClick = (timeStr, availabilityRefId) => {
    setSelectedTime({ time: timeStr, availabilityRefId });
    setSelectedPerformance(timeStr);
    setSelectedAttribute(null);
    setLoadingAttributes(true);

    const selectedInterval = intervals.find((item) => format(item.time, 'HH:mm') === timeStr);

    if (!selectedInterval || !Array.isArray(selectedInterval.attributes)) {
      setAvailableAttributes([]);
      setSelectedAttribute(null);
      setLoadingAttributes(false);
      return;
    }

    const finalAttrs = [...new Set(selectedInterval.attributes)];
    setAvailableAttributes(finalAttrs);
    setTimeout(() => {
      if (swiperAttributesRef.current?.swiper) {
        swiperAttributesRef.current.swiper.update();
        if (finalAttrs.length > 1) {
          swiperAttributesRef.current.swiper.slideTo(0);
        }
      }
      setLoadingAttributes(false);
    }, 100);
    if (finalAttrs.length === 1) {
      setSelectedAttribute(finalAttrs[0]);
    }
  };

  const onAttributeSelect = (attr) => {
    setSelectedAttribute(attr);
  };

  const handleSpecialRequestChange = (e) => {
    setSpecialRequest(e.target.value);
  };

const renderBookSelector = () => {
    return (
      <div className='mt-1 pt-3 mb-3'>
        <div className='book-selector-container'>
          <BookSelectorModify
            selectedDateBook={selectedDateBook}
            today={today}
            lang={language}
            onChange={onBookSelectorChange}
            paxs={reservationInfo.paxs}
          />

          {loadingIntervals ? (
            <div className='mt-3 mb-3'>
              <Swiper
                spaceBetween={10}
                slidesPerView={'auto'}
                centeredSlides={true}
                centeredSlidesBounds={true}
                ref={swiperTimeRef}
              >
                {[...Array(5)].map((_, i) => (
                  <SwiperSlide key={i}>
                    <div
                      style={{
                        backgroundColor: '#191919',
                        borderRadius: '5px',
                        height: '38px',
                        width: '78px',
                      }}
                    ></div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          ) : (
            <>
              <div className='mt-3 mb-3'>
                <Swiper
                  spaceBetween={10}
                  slidesPerView={'auto'}
                  centeredSlides={true}
                  centeredSlidesBounds={true}
                  ref={swiperTimeRef}
                >
                  {intervals.length === 0
                    ? [...Array(5)].map((_, i) => (
                        <SwiperSlide key={i}>
                          <button className='button-filter empty font-small'></button>
                        </SwiperSlide>
                      ))
                    : intervals
                        .filter((item) =>
                          selectedAttribute
                            ? Array.isArray(item.attributes) &&
                              item.attributes.includes(selectedAttribute)
                            : true,
                        )
                        .map((item, i) => (
                          <SwiperSlide key={i}>
                            <button
                              className={`button-filter font-small ${
                                selectedTime?.time === format(item.time, 'HH:mm') ? 'active' : ''
                              }`}
                              onClick={() =>
                                onTimeClick(format(item.time, 'HH:mm'), item.availabilityRefId)
                              }
                            >
                              {format(item.time, 'h:mm aaa')}
                            </button>
                          </SwiperSlide>
                        ))}
                </Swiper>
              </div>

              {selectedTime && availableAttributes.length > 1 && (
                <>
                  <div
                    className='mb-2'
                    style={{
                      borderTop: '1px solid #262835',
                    }}
                  >
                    <div className='font-1rem fw-600 mt-2'>{t('seatType')}</div>
                  </div>

                  <div>
                    <Swiper
                    ref={swiperAttributesRef}
                      spaceBetween={10}
                      slidesPerView={'auto'}
                      centeredSlides={true}
                      centeredSlidesBounds={true}
                    >
                      {availableAttributes.map((attr) => {
                        const displayText =
                          attributeTranslations[attr] ||
                          attr.charAt(0).toUpperCase() + attr.slice(1);

                        return (
                          <SwiperSlide key={attr} style={{ width: 'auto' }}>
                            <button
                              className={`button-filter font-small ${
                                selectedAttribute === attr ? 'active' : ''
                              }`}
                              style={{
                                minWidth: '90px',
                                width: 'auto',
                                padding: '0 16px',
                                whiteSpace: 'nowrap',
                              }}
                              onClick={() => onAttributeSelect(attr)}
                            >
                              {displayText}
                            </button>
                          </SwiperSlide>
                        );
                      })}
                    </Swiper>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const handleModify = async () => {
    if (!selectedPerformance) return;

    setModifyingReservation(true);
    setScheduleNotAvailable(false);
    setErrorMessage(null);

    try {
      let availabilityRefId = null;
      if (platForm) {
        const lockResult = await lockSchedule(welletContext, {
          pax: selectedPax,
          reservationDateLocalTime: `${selectedDate}T${selectedPerformance}`,
          reservationType: 'RESTAURANT',
          showId: restaurantId,
          attribute: selectedAttribute ?? null,
        });

        if (!lockResult?.success || !lockResult?.reservation_Token) {
          setScheduleNotAvailable(true);
          setModifyingReservation(false);
          return;
        }
        availabilityRefId = lockResult.reservation_Token;
      }

      const response = await welletContext.apis.tickets.put('/reservation/ModifyReservation', {
        showId: restaurantId,
        paxs: selectedPax,
        CustomerName: reservationInfo?.customerName ?? '',
        CustomerPhone: reservationInfo?.customerPhone ?? '',
        Email: reservationInfo?.customerEmail ?? '',
        CountryCode: 'MX',
        ReservationDateLocalTime: `${selectedDate} ${selectedPerformance}`,
        OldReservationId: reservationInfo.reservationId,
        comments: specialRequest || '',
        attribute: selectedAttribute,
        ...(availabilityRefId && { availabilityRefId }),
      });

      if (response?.status === 200) {
        setResponse(true);
      }
    } catch (error) {
      console.error(error);
      const errorType = error.response?.data?.errorType;
      switch (errorType) {
        case 'RESERVATION_NOT_FOUND':
          setErrorMessage(t('modifyReservationError.RESERVATION_NOT_FOUND'));
          break;
        case 'RESERVATION_NOT_ALLOWED_FOR_MODIFY':
          setErrorMessage(t('modifyReservationError.RESERVATION_NOT_ALLOWED_FOR_MODIFY'));
          break;
        case 'EXCEEDED_TIME_FOR_MODIFICATION':
          setErrorMessage(t('modifyReservationError.EXCEEDED_TIME_FOR_MODIFICATION'));
          break;
        case 'HAS_MODIFICATION':
          setErrorMessage(t('modifyReservationError.HAS_MODIFICATION'));
          break;
        case 'OLD_ORDER_ID_REQUIRED':
          setErrorMessage(t('modifyReservationError.OLD_ORDER_ID_REQUIRED'));
          break;
        case 'NO_AVAILABILITY':
          setErrorMessage(t('reservationError.NO_AVAILABILITY'));
          break;
        case 'DUPLICATE_RESERVATION':
          setErrorMessage(t('reservationError.DUPLICATE_RESERVATION'));
          break;
        default:
          setErrorMessage(t('modifyReservationError.DEFAULT'));
      }
    } finally {
      setModifyingReservation(false);
    }
  };

  return (
    <>
      {response ? (
        <SuccessScreen
          title={`${t('done')}!`}
          subtitle={`${t('modifyReservationSuccess')}`}
          path={'/'}
        />
      ) : (
        <>
          <Header title={<div className='text-capitalize px-3'>{t('modifyReservation')}</div>} />
          <ContentApp classes='p-0 d-flex flex-column' menu={true} style={{ height: '100vh' }}>
            {!loading &&
            compareToday !== null &&
            firstScheduleLoaded &&
            (!platForm || externalPlaftorm) ? (
              <>
                <div className='flex-grow-1 overflow-auto mr-3 ml-3 pb-4'>
                  <EventHeader
                    eventImg={reservationInfo?.showLogoImageUrl}
                    eventName={reservationInfo?.showName}
                    eventDescription={reservationInfo?.showName}
                    eventDate={null}
                    size='small'
                    paxs={''}
                  />
                  <div className='fw-semibold text-medium text-secondary mt-3'>
                    {t('bookingHolder')}
                  </div>
                  <div className='font-normal bold mt-1'>{reservationInfo.customerName}</div>
                  <div className='mt-3'>
                    <CustomerSpecialRequest
                      values={{ comments: specialRequest || '' }}
                      handleChange={handleSpecialRequestChange}
                    />
                  </div>
                </div>

                <div
                  className='w-100 position-fixed pb-3'
                  style={{
                    bottom: '0',
                    left: 0,
                    maxHeight: 'auto',
                    overflowY: 'auto',
                    borderTopLeftRadius: 20,
                    borderTopRightRadius: 20,
                    backgroundColor: '#000',
                    paddingBottom: 'env(safe-area-inset-bottom)',
                    zIndex: 2,
                  }}
                >
                  <div className='container mb-3'>
                    {renderBookSelector()}

                    <div className='row justify-content-center'>
                      <div className='col-12 col-md-7 col-lg-6'>
                        {(errorMessage || scheduleNotAvailable) && (
                          <div className='mb-3'>
                            <ErrorBox
                              title={
                                scheduleNotAvailable ? t('scheduleIsNotAvailable') : errorMessage
                              }
                            />
                          </div>
                        )}
                        <Button
                          text={
                            modifyingReservation ? (
                              <Spinner size={25} color='#000' />
                            ) : (
                              t('confirmChanges')
                            )
                          }
                          size='large'
                          className={`mb-3 ${styles.btn}`}
                          onClick={handleModify}
                          disabled={
                            !selectedPax ||
                            !selectedPerformance ||
                            modifyingReservation ||
                            (availableAttributes.length > 1 && !selectedAttribute)
                          }
                        />
                      </div>
                    </div>

                    {platForm && externalPlaftorm && (
                      <>
                        <div className='text-secondary font-tiny mb-3 px-2'>
                          {t('opentableTermsAndConditions.clickingRequestBooking')}{' '}
                          <a
                            href='https://www.opentable.com.mx/c/legal/terms-and-conditions/'
                            className='text-primary'
                            target='_blank'
                            rel='noopener noreferrer'
                          >
                            {t('opentableTermsAndConditions.termsOfUse')}
                          </a>
                          {t('opentableTermsAndConditions.andThe')}{' '}
                          <a
                            href='https://www.opentable.com.mx/c/legal/privacy-policy/'
                            className='text-primary'
                            target='_blank'
                            rel='noopener noreferrer'
                          >
                            {t('opentableTermsAndConditions.privacyPolicy')}
                          </a>
                          {t('opentableTermsAndConditions.openTable')}
                        </div>
                        <div className='text-center mb-3'>
                          <span className='semi-bold mr-1 font-smaller'>Powered by&nbsp;</span>
                          <img
                            className={styles.containerImgLogoPlatform}
                            src={externalPlaftorm?.logoUrl}
                            alt='logo-restaurant'
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className='loaderDiv d-flex align-items-center justify-content-center flex-grow-1'>
                <CircleSpinner
                  className='spinner-app mx-auto'
                  style={{ margin: 'auto' }}
                  size={40}
                  color='var(--color-font-primary)'
                />
              </div>
            )}
          </ContentApp>
        </>
      )}
    </>
  );
};

export default ModifyReservation;
